import { decodeToken } from '../services/authApi';

export interface AnalyticsNavigationUser {
  profile?: string;
  role?: string;
}

export interface AnalyticsNavigationState {
  fromAgency?: boolean;
}

export interface AnalyticsQuickLink {
  text: string;
  icon: string;
  path: string;
}

function getEffectiveUserContext(user: AnalyticsNavigationUser | null | undefined) {
  const token = typeof window !== 'undefined' ? window.localStorage.getItem('token') : null;
  const tokenClaims = token ? decodeToken(token) : null;

  return {
    profile: user?.profile ?? tokenClaims?.profile,
    role: user?.role ?? tokenClaims?.role,
  };
}

export function isCustomerProfile(user: AnalyticsNavigationUser | null | undefined) {
  const profile = getEffectiveUserContext(user).profile?.toLowerCase();
  return profile === 'customer' || profile === 'customers';
}

export function isAgencyScopedProfile(user: AnalyticsNavigationUser | null | undefined) {
  const { profile, role } = getEffectiveUserContext(user);
  const normalizedProfile = profile?.toLowerCase();
  const normalizedRole = role?.toLowerCase();

  return Boolean(
    normalizedProfile && (
      normalizedProfile === 'agency-admin' ||
      normalizedProfile === 'agency-analyst' ||
      normalizedProfile === 'agency' ||
      normalizedProfile.startsWith('agency')
    )
  ) || Boolean(normalizedRole && (normalizedRole.includes('agency') || normalizedRole.includes('admin')));
}

export function getDefaultAnalyticsPath(user: AnalyticsNavigationUser | null | undefined) {
  if (isAgencyScopedProfile(user)) {
    return '/analytics/agency';
  }

  return '/analytics/customer';
}

export function getAnalyticsQuickLinks(
  user: AnalyticsNavigationUser | null | undefined,
  siteId?: string | number | null,
): AnalyticsQuickLink[] {
  const customerPath = siteId ? `/analytics/customer/${siteId}` : '/analytics/customer/default';

  if (isCustomerProfile(user)) {
    return [{ text: 'Customer Dashboard', icon: 'person', path: customerPath }];
  }

  if (!isAgencyScopedProfile(user)) {
    return [{ text: 'Customer Dashboard', icon: 'person', path: customerPath }];
  }

  return [
    { text: 'Agency Dashboard', icon: 'business', path: '/analytics/agency' },
    { text: 'Customer Dashboard', icon: 'person', path: customerPath },
  ];
}

export function shouldShowAgencyBackLink(
  routeSiteId?: string | null,
  state?: unknown,
) {
  if (typeof state === 'object' && state !== null && 'fromAgency' in state) {
    return Boolean((state as AnalyticsNavigationState).fromAgency);
  }

  return Boolean(routeSiteId);
}
